import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ReportService } from '../../../../../../../../shared/services/report.service';
import { projectDetails, submittalsItemsListInfo } from '../../../../../../core/projects.selectors';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ClearSubmittalsItemDetailsWithId, FetchSubmittalsItemsListRequest } from '../../../../../../core/projects.actions';
import { CreateOrEditSubmittalsItemComponent } from '../create-or-edit-submittals-item/create-or-edit-submittals-item.component';
import { AuthService } from '../../../../../../../../shared/services';
import { FormControl, FormGroup } from '@angular/forms';
import { SubmittalsService } from '../submittals.service';
import { Report } from '../../../../../../../../shared/models';
import * as moment from 'moment-timezone';

@Component({
    selector: 'app-submittals-list',
    templateUrl: './submittals-list.component.html',
    styleUrls: ['./submittals-list.component.scss'],
    standalone: false
})
export class SubmittalsListComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: any;
  @ViewChild('sortSelect') sortSelect: any;
  isSearchFocused = false;
  defaultDateValue = 'All time';
  displayedColumns: string[] = ['submittalNo', 'specRefNo', 'title', 'sentDate', 'dueDate', 'status'];
  sortByColumns: string[] = ['Submittal no', 'Spec ref no', 'Title', 'Sent date', 'Due date', 'Status'];
  selectedSortColumn: string = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  statusMap = {
    submitted: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
    rejected: {
      label: 'Rejected',
      bgColor: 'bg-rejected',
      textColor: 'txt-rejected',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
    draft_revision: {
      label: 'Draft (revision)',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
    sent_for_review: {
      label: 'Sent for review',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    revise_and_resubmit: {
      label: 'Revise and resubmit',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    approved_as_noted: {
      label: 'Approved as noted',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    no_objection: {
      label: 'Reviewed for information only',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    voided: {
      label: 'Voided',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
  };
  projectId: string;
  selectedRow: any;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;
  account: any;
  role: any;
  contractorRole: any;
  startDate = null;
  endDate = null;
  statusIdsToFilter: any[] = [];
  searchText = '';
  projectTimezone: string;
  form = new FormGroup({
    searchText: new FormControl(''),
  });
  statusList = [
    {
      label: 'Awaiting Submission',
      value: 'draft',
      isChecked: false,
    },
    {
      label: 'Draft (revision)',
      value: 'draft_revision',
      isChecked: false,
    },
    {
      label: 'Awaiting Approval',
      value: 'submitted',
      isChecked: false,
    },
    {
      label: 'Approved',
      value: 'approved',
      isChecked: false,
    },
    {
      label: 'Rejected',
      value: 'rejected',
      isChecked: false,
    },
    {
      label: 'Sent for review',
      value: 'sent_for_review',
      isChecked: false,
    },
    {
      label: 'Revise and resubmit',
      value: 'revise_and_resubmit',
      isChecked: false,
    },
    {
      label: 'Approved as noted',
      value: 'approved_as_noted',
      isChecked: false,
    },
    {
      label: 'Reviewed for information only',
      value: 'no_objection',
      isChecked: false,
    },
    {
      label: 'Voided',
      value: 'voided',
      isChecked: false,
    },
  ];
  downloading = false;
  submittalReport: Report = null;
  /**
   * Deep link from global search: `?submittalsItemId=` (the existing param
   * rowClicked writes and the right panel reads) auto-opens the submittal
   * details panel on load, one-shot, the same way a row click would.
   */
  private pendingDeepLinkId: string = null;
  /** False until the store's synchronous replay of the previous list state
   * has passed; deep-link consumption only trusts fresh emissions. */
  private listReady = false;
  private readonly onDestroy = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private submittalsService: SubmittalsService,
    private cdRef: ChangeDetectorRef,
    private reportService: ReportService,
    private snackBar: MatSnackBar,
  ) {
  }

  ngOnInit(): void {
    this.pendingDeepLinkId = this.route.snapshot.queryParamMap.get('submittalsItemId');

    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.account = this.authService.getCurrentUser();
      this.role = this.account.roles.find(obj => obj.project_id === this.projectId)?.role;
      this.contractorRole = !!this.role?.includes('contractor');

      if (this.role === 'project-manager') {
        if (!this.displayedColumns.includes('latestActivity')) {
          this.displayedColumns.push('latestActivity');
        }
        if (!this.sortByColumns.includes('Latest activity')) {
          this.sortByColumns.push('Latest activity');
        }
      } else {
        this.displayedColumns = this.displayedColumns.filter(
          column => column !== 'latestActivity',
        );
        this.sortByColumns = this.sortByColumns.filter(c => c !== 'Latest activity');
      }


      if (this.role?.includes('contractor')) {
        this.statusList = this.statusList.filter(o => o.value !== 'draft_revision');
      } else {
        this.statusList = this.statusList.filter(o => o.value !== 'draft');
      }

      if (this.projectId) {
        this.fetchSubmittalsItemsList();
        // Report templates aren't accessible to contractors (the /v2 reports endpoint 403s for
        // them); only fetch the submittal report template for roles that can read it.
        if (!this.role?.includes('contractor')) {
          this.loadSubmittalReport();
        }
      }

      this.setDefaultStatusFilters();
    }

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        // Don't refetch the list here. The full list is loaded once on init (and the component is
        // recreated whenever the user enters the Log sub-tab), and the store is refreshed by the
        // effects after any create/edit/delete. Refetching on every query-param change re-ran the
        // unpaginated 1000-item call on unrelated changes like row selection (snSubTab/submittalsItemId)
        // and status/date filters — all of which only affect the client-side filtering below.

        if (qp.startDate && moment(qp.startDate).isValid()) {
          this.startDate = qp.startDate;
        } else {
          this.startDate = null;
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          this.endDate = qp.endDate;
        } else {
          this.endDate = null;
        }

        if (qp.status) {
          if (Array.isArray(qp.status)) {
            this.statusIdsToFilter = qp.status;
          } else {
            this.statusIdsToFilter = [qp.status];
          }
        } else {
          this.statusIdsToFilter = [];
        }

        this.applyFilters();
      });

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.searchText = data || '';
        this.applyFilters();
      });

    this.store
      .select(submittalsItemsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.dataSource = [];
        this.originalDataSource = [];
        this.filteredDataSource = [];
        this.totalRecords = 0;
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {

            if (!this.role?.includes('contractor')) {
              data.list = data?.list.filter(o => o.status !== 'draft' || this.isRevisionDraft(o));
            }

            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;

            // Each emission deep-clones the rows, so re-point the reference
            // the highlighted-row styling compares against.
            if (this.selectedRow?.id) {
              const current = this.originalDataSource.find(row => row?.id === this.selectedRow.id);
              if (current) {
                this.selectedRow = current;
              }
            }

            this.applyFilters();
          }

          // Skip the synchronous replay of the previous store state (possibly
          // another project's list): only fresh fetch responses may consume
          // the deep link — otherwise the id "misses" and falls back to a
          // fetch-by-id without ever surfacing the row in the list.
          if (this.listReady) {
            this.tryOpenDeepLinkedSubmittal();
          }
        }

        this.cdRef.detectChanges();
      });
    this.listReady = true;

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.timezone) {
          this.projectTimezone = data.timezone;
        }
      });
  }

  fetchSubmittalsItemsList(): void {
    const payload = {
      start: this.pagination?.pageIndex * this.pagination?.pageSize,
      total: 1000,
      include: [],
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchSubmittalsItemsListRequest({ payload }));
    }
  }

  /**
   * Auto-opens the details panel for a deep-linked submittal
   * (?submittalsItemId=) once the list has loaded, reusing the row-click data
   * path. If the submittal isn't in the fetched list (e.g. a draft hidden from
   * this role), it is fetched by id instead; an unknown/404 id no-ops silently.
   */
  private tryOpenDeepLinkedSubmittal(): void {
    if (!this.pendingDeepLinkId) {
      return;
    }
    const id = this.pendingDeepLinkId;
    this.pendingDeepLinkId = null;

    const match = this.originalDataSource.find(row => `${row?.id}` === `${id}`);
    if (match) {
      this.rowClicked(match);
      this.revealDeepLinkedRow(match);
      return;
    }

    this.projectsService.fetchSubmittalsItemDetailsWithId({ submittalsItemId: id })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          const record = resp?.data;
          if (record?.id) {
            this.rowClicked(record);
          }
        },
        error: () => {
          // Deep-linked submittal no longer exists (or is not accessible) — stay on the log.
        },
      });
  }

  /**
   * Surfaces the deep-linked row in the left list: clears any blocking
   * status/date filters (e.g. the role-based default status filters, which
   * hide approved/closed submittals), jumps to the row's page and scrolls it
   * into view (the highlighted-row styling comes from rowClicked's
   * selectedRow). Timers let rowClicked's queryParams merge (whose
   * applyFilters pass resets pagination) settle first.
   */
  private revealDeepLinkedRow(row): void {
    setTimeout(() => {
      const blocked = this.filteredDataSource.indexOf(row) < 0
        && !!(this.statusIdsToFilter?.length || this.startDate || this.endDate);
      const nav = blocked
        ? this.router.navigate([], {
          queryParams: { status: null, startDate: null, endDate: null },
          queryParamsHandling: 'merge',
        })
        : Promise.resolve(true);

      nav.then(() => setTimeout(() => {
        const index = this.filteredDataSource.indexOf(row);
        if (index < 0) {
          return;
        }
        this.currentPage = Math.floor(index / this.itemsPerPage) + 1;
        this.pagination.pageIndex = this.currentPage - 1;
        this.paginateData();
        setTimeout(() => {
          document.querySelector('app-submittals-list .highlighted-row')
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
      }));
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditSubmittalsItemComponent,
      data: { ...row },
    });

    this.router.navigate([], {
      queryParams: {
        snSubTab: 'details',
        submittalsItemId: row.id,
      },
      queryParamsHandling: 'merge',
    });
  }

  create(): void {
    // A new panel must not inherit the last-viewed submittal. The panel only
    // clears the details slice when ITS url id disappears, and the X close
    // destroys the panel before that navigation lands, so the slice keeps the
    // last-opened record and the fresh Create panel replays it as an edit.
    // Clear the slice here and drop any open row's id from the url so the new
    // panel doesn't refetch it either (same as rfis-list create()).
    this.store.dispatch(ClearSubmittalsItemDetailsWithId());
    this.selectedRow = null;
    this.router.navigate([], {
      queryParams: { submittalsItemId: null, snSubTab: null },
      queryParamsHandling: 'merge',
    });

    this.projectsService.openRightPanel.next({
      component: CreateOrEditSubmittalsItemComponent,
      data: {},
    });
  }

  onPageChanged(page: number): void {
    this.projectsService.closeRightPanel.next();
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  search() {
    const { searchText } = this.form.getRawValue();
    this.submittalsService.search.next(searchText);
  }

  close() {
    this.form.reset();
    const searchText = '';
    this.isSearchFocused = false;
    this.submittalsService.search.next(searchText);
  }

  applyFilters() {
    let filteredData = [...this.originalDataSource];

    if (this.startDate) {
      const start = moment(this.startDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data =>
        moment(data.due_date, 'YYYY-MM-DD').isSameOrAfter(start),
      );
    }

    if (this.endDate) {
      const end = moment(this.endDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data =>
        moment(data.due_date, 'YYYY-MM-DD').isSameOrBefore(end),
      );
    }

    if (this.statusIdsToFilter?.length) {
      const hasDraftRevisionFilter = this.statusIdsToFilter.includes('draft_revision');
      const normalStatuses = this.statusIdsToFilter.filter(s => s !== 'draft_revision');

      filteredData = filteredData.filter(data => {
        if (hasDraftRevisionFilter && data.status === 'draft' && this.isRevisionDraft(data)) {
          return true;
        }
        return normalStatuses.length > 0 && normalStatuses.includes(data.status);
      });
    }

    if (this.searchText.trim()) {
      const search = this.searchText.trim().toLowerCase();
      filteredData = filteredData.filter(data =>
        (
          data.description?.toLowerCase().includes(search) ||
          data.title?.toLowerCase().includes(search) ||
          data.submittal_no?.toLowerCase().includes(search)
        ),
      );
    }

    filteredData = this.sortItems(filteredData);

    this.filteredDataSource = filteredData;
    this.totalRecords = filteredData.length;
    this.paginateData(true);
  }

  onSearchFocus() {
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    this.isSearchFocused = true;
  }

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    this.sortSelect?.open?.();
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event.value;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  setDefaultStatusFilters() {
    if (!!this.role?.includes('contractor')) {
      this.router.navigate([], {
        queryParams: {
          status: ['draft', 'submitted', 'sent_for_review'],
        },
        queryParamsHandling: 'merge',
      });
    }

    if (!!this.role?.includes('project-manager')) {
      this.router.navigate([], {
        queryParams: {
          status: ['submitted', 'sent_for_review'],
        },
        queryParamsHandling: 'merge',
      });
    }

    if (!!this.role?.includes('reviewer')) {
      this.router.navigate([], {
        queryParams: {
          status: ['sent_for_review'],
        },
        queryParamsHandling: 'merge',
      });
    }

  }

  /**
   * Format a timestamp in the project timezone
   * Treats timestamps without timezone indicator as UTC
   */
  formatInProjectTimezone(timestamp: string, format: string): string {
    if (!timestamp) return '';
    const tz = this.projectTimezone || 'UTC';
    return moment.utc(timestamp).tz(tz).format(format);
  }

  async loadSubmittalReport(): Promise<void> {
    try {
      const reports = await this.reportService.getReports(
        { id: this.projectId } as any,
        null,
        {}
      );
      this.submittalReport = reports.find(r => r.name === 'Submittal summary report') || null;
    } catch (error) {
      console.error('Failed to load Submittal report template:', error);
    }
  }

  async downloadSubmittalReport(): Promise<void> {
    if (!this.submittalReport || this.downloading) {
      return;
    }

    this.downloading = true;

    try {
      const params: any = {
        projectId: this.projectId,
      };

      if (this.statusIdsToFilter && this.statusIdsToFilter.length > 0) {
        params.entityStatus = this.statusIdsToFilter;
      }

      if (this.startDate) {
        params.startDate = moment(this.startDate).format('YYYY-MM-DD');
      }

      if (this.endDate) {
        params.endDate = moment(this.endDate).format('YYYY-MM-DD');
      }

      await this.reportService.downloadReport(
        this.submittalReport,
        null,
        { id: this.projectId },
        params,
        false,
        false
      );

      this.snackBar.open('Report downloaded successfully', '', { duration: 3000 });
    } catch (error) {
      console.error('Failed to download Submittal report:', error);
      this.snackBar.open('Failed to download report', '', { duration: 5000 });
    } finally {
      this.downloading = false;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private isRevisionDraft(item: any): boolean {
    const match = item.submittal_no?.match(/^(\d+)-Rev(\d+)/i);
    return match ? parseInt(match[2], 10) > 0 : false;
  }

  private sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const itemsToSort = [...items];

    const safeLower = (v: any) => String(v ?? '').toLowerCase();
    const safeDate = (v: any) => {
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    // Natural sort for submittal numbers like "1-Rev0", "2-Rev0", "10-Rev0"
    const naturalSortSubmittalNo = (a: any, b: any): number => {
      const aVal = String(a.submittal_no ?? '');
      const bVal = String(b.submittal_no ?? '');

      // Extract number and revision: "1-Rev0" -> { num: 1, rev: 0 }
      const parseSubmittalNo = (val: string) => {
        const match = val.match(/^(\d+)(?:-Rev(\d+))?/i);
        if (match) {
          return {
            num: parseInt(match[1], 10),
            rev: match[2] ? parseInt(match[2], 10) : 0,
          };
        }
        return { num: 0, rev: 0, str: val };
      };

      const aParsed = parseSubmittalNo(aVal);
      const bParsed = parseSubmittalNo(bVal);

      // Sort by number first, then by revision
      if (aParsed.num !== bParsed.num) {
        return aParsed.num - bParsed.num;
      }
      return aParsed.rev - bParsed.rev;
    };

    if (!this.selectedSortColumn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const sortFns: Record<string, (a: any, b: any) => number> = {
      'Submittal no': naturalSortSubmittalNo,
      'Spec ref no': (a, b) => safeLower(a.spec_reference_no).localeCompare(safeLower(b.spec_reference_no)),
      'Title': (a, b) => safeLower(a.title).localeCompare(safeLower(b.title)),
      'Sent date': (a, b) => safeDate(a.sent_date) - safeDate(b.sent_date),
      'Due date': (a, b) => safeDate(a.due_date) - safeDate(b.due_date),
      'Status': (a, b) => safeLower(a.status).localeCompare(safeLower(b.status)),
      'Latest activity': (a, b) => safeDate(a?.latest_lineage?.created_at) - safeDate(b?.latest_lineage?.created_at),
    };

    const fn = sortFns[this.selectedSortColumn];
    if (!fn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const applyDirection = (result: number) => (this.sortDirection === 'asc' ? result : -result);

    return itemsToSort.sort((a, b) => {
      const primary = fn(a, b);
      if (primary !== 0) {
        return applyDirection(primary);
      }
      return safeDate(b.updated_at) - safeDate(a.updated_at);
    });
  }
}
